import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  TextField,
  Collapse,
  Box,
} from "@mui/material";
import useAILetterFormatter from "./useAILetterFormatter";
import { showError } from "../../toastify";

const AILetterFormatterButton = ({ content, context = "", onFormatted }) => {
  const { formatLetter, isFormatting } = useAILetterFormatter(context);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [userPrompt, setUserPrompt] = useState(`Rewrite and improve the following into a formal, well-structured authorization letter suitable for official use. Ensure proper grammar, professional tone, and clear formatting (with subject line and paragraphs):
[paste your content here]`);
  const [previousContent, setPreviousContent] = useState(null);
  const [promptError, setPromptError] = useState(false);
  const isPromptEmpty = !userPrompt.trim();

  const handleAskAI = () => {
    setPreviousContent(content);
    setShowPromptInput(true);
  };

  const handleConfirm = async () => {
    if (!userPrompt.trim()) {
      setPromptError(true);
      return;
    }
    setPromptError(false);
    try {
      const formatted = await formatLetter(content, userPrompt);
      onFormatted(formatted);
    } catch {
      showError("AI formatting failed. Please check the server and try again.");
    }
  };

  const handleReset = () => {
    if (previousContent !== null) {
      onFormatted(previousContent);
    }
    setShowPromptInput(false);
    setUserPrompt("");
    setPreviousContent(null);
    setPromptError(false);
  };

  return (
    <Box sx={{ mb: 1 }}>
      {!showPromptInput && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!content}
            onClick={handleAskAI}
            startIcon={
              <span className="material-icons" style={{ fontSize: "16px" }}>
                auto_fix_high
              </span>
            }
            sx={{
              fontSize: "0.7rem",
              textTransform: "none",
              borderColor: "rgb(7, 142, 206)",
              color: "rgb(7, 142, 206)",
            }}
          >
            Ask AI
          </Button>
        </Box>
      )}

      <Collapse in={showPromptInput}>
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            required
            placeholder='e.g. "Make it more concise" or "Use a more formal tone"'
            value={userPrompt}
            error={promptError}
            helperText={promptError ? "Please enter your instructions before confirming." : ""}
            onChange={(e) => {
              setUserPrompt(e.target.value);
              if (promptError && e.target.value.trim()) setPromptError(false);
            }}
            sx={{ fontSize: "0.8rem" }}
            inputProps={{ style: { fontSize: "0.8rem" } }}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={isFormatting}
              onClick={handleReset}
              sx={{ fontSize: "0.7rem", textTransform: "none" }}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={isFormatting || !content || isPromptEmpty}
              onClick={handleConfirm}
              startIcon={
                isFormatting ? (
                  <CircularProgress size={14} sx={{ color: "white" }} />
                ) : (
                  <span className="material-icons" style={{ fontSize: "16px" }}>
                    auto_fix_high
                  </span>
                )
              }
              sx={{
                fontSize: "0.7rem",
                textTransform: "none",
                backgroundColor: "rgb(7, 142, 206)",
                "&:hover": { backgroundColor: "rgb(5, 120, 180)" },
              }}
            >
              {isFormatting ? "Formatting…" : "Confirm"}
            </Button>
          </Box>
          {isPromptEmpty && !isFormatting && (
            <Box sx={{ mt: 0.5, textAlign: "right", fontSize: "0.72rem", color: "text.secondary" }}>
              Enter instructions to enable Confirm.
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AILetterFormatterButton;
